import { Logger } from '~~/helpers/logger';

import { EXPONDO_MIDDLEWARE_ROUTES } from '~~/constants/routes';
import { extractLocalizedCustomField } from '~~/helpers/customFields';
import { getDiscountKeyForTier } from '~~/helpers/getDiscountKeyForTier';
import { getLoyaltyLevelNumber } from '~~/helpers/getLoyaltyLevelNumber';
import { CUSTOMER_GROUP_CUSTOM_FIELDS } from '~~/constants/commerceTools';
import type {
  LoyaltyCartDiscount,
  LoyaltyCustomerGroup,
  LoyaltyLevel,
  LoyaltyLevelsRawResponse,
} from '~~/types/loyaltyLevel/loyaltyLevel';

/**
 * Loyalty levels as configured in commercetools: the levels active in the current store, each with its
 * discount percentage and the old spend range from the customer group. Shop-wide (cart, product page,
 * CMS), loaded during server rendering. For points thresholds see useCDPLoyaltyLevels.
 *
 * Kept separate from useCDPLoyaltyLevels on purpose: commercetools both stores and applies the loyalty
 * discounts, so it stays their source of truth; the Customer Data Platform only knows the thresholds.
 */
export default () => {

  const { makeCommercetoolsRequest } = useExpondoMiddleware();
  const { languageAndCountry, countryLocalization, countryCode } = useLocalization();

  const loyaltyLevels = useState<LoyaltyLevel[] | null>('loyaltyLevels', () => null);
  const loading = useState<boolean>('loyaltyLevelsLoading', () => false);

  const getSpendingRange = (customerGroup: LoyaltyCustomerGroup): LoyaltyLevel['spendingRange'] => {

    const spendingRangeFrom = extractLocalizedCustomField(
      customerGroup,
      CUSTOMER_GROUP_CUSTOM_FIELDS.COHORT_RANGE_FROM,
      languageAndCountry.value,
    );

    const spendingRangeTo = extractLocalizedCustomField(
      customerGroup,
      CUSTOMER_GROUP_CUSTOM_FIELDS.COHORT_RANGE_TO,
      languageAndCountry.value,
    );

    return {
      from: spendingRangeFrom ? Number.parseFloat(spendingRangeFrom) : 0,
      to: spendingRangeTo ? Number.parseFloat(spendingRangeTo) : undefined,
    };
  
  };

  const isDiscountActiveInStore = (discount: LoyaltyCartDiscount, store: string): boolean => {

    const isStoreValid = discount?.storesRef?.some(
      (storeRef) => storeRef.key === store,
    );

    return isStoreValid && discount?.isActive;
  
  };

  const parseLevelsData = (data: LoyaltyLevelsRawResponse | undefined): LoyaltyLevel[] => {

    const levels: Record<string, LoyaltyLevel> = {};

    if (!data) {

      return [];
    
    }

    data.customerGroups.results.forEach((customerGroup) => {

      const cohortKey = customerGroup.key;
      const discountKey = getDiscountKeyForTier(cohortKey, countryCode.value);
      const cohortDiscount = data.cartDiscounts.results.find(d => d.key === discountKey);

      if (cohortDiscount && !isDiscountActiveInStore(cohortDiscount, countryLocalization.value.storeId)) {

        return;
      
      }
      const discountPercentage = cohortDiscount ? cohortDiscount.value.permyriad / 100 : 0;

      const spendingRange = getSpendingRange(customerGroup);
      const levelNumber = getLoyaltyLevelNumber(cohortKey);

      levels[cohortKey] = {
        levelNumber,
        spendingRange,
        discountPercentage,
      };
    
    });

    return Object.values(levels).sort((a, b) => a.levelNumber - b.levelNumber);
  
  };

  const load = async () => {

    if (loyaltyLevels.value !== null) {

      return;
    
    }

    if (loading.value) {

      return;
    
    }

    loading.value = true;
    try {

      const res = await makeCommercetoolsRequest<LoyaltyLevelsRawResponse>({
        endpoint: EXPONDO_MIDDLEWARE_ROUTES.CT.GET_LOYALTY_LEVELS,
      });

      loyaltyLevels.value = parseLevelsData(res?.data);
    
    } catch (err) {

      Logger.error('error.useCTLoyaltyLevels', String(err));
    
    } finally {

      loading.value = false;
    
    }
  
  };

  const highestLoyaltyDiscountPercentage = computed<number | null>(() => {

    if (!loyaltyLevels.value?.length) {

      return null;
    
    }

    return Math.max(...loyaltyLevels.value.map(level => level.discountPercentage));
  
  });

  return {
    load,
    loading,
    loyaltyLevels,
    highestLoyaltyDiscountPercentage,
  };

};
